import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useDatabaseContent } from '@databases/database-state';
import PanelLayout from '@databases/panel/PanelLayout';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { ViewId } from '@databases/types/database/view';
import { getDatabaseFields } from '@databases/utils/database/utils';
import { getViewById } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import useDeleteView from '../../hooks/useDeleteView';
import { useSaveAsNewView } from '../../hooks/useSaveAsNewView';
import { useUpdateView } from '../../hooks/useUpdateView';
import useView from '../../hooks/useView';
import { useViewHasUnsavedChanges } from '../../hooks/useViewHasUnsavedChanges';
import { useViewUiState } from '../../hooks/useViewUiState';
import { messages } from '../../i18n';
import { ViewPanelSettingType } from '../../types/types';
import { isUniqueViewName } from '../../utils/viewName';
import BackButton from '../viewSettingsActions/BackButton';
import CopyLinkButton from '../viewSettingsActions/CopyLinkButton';
import DeleteViewButton from '../viewSettingsActions/DeleteViewButton';
import ViewSettingsFields from '../viewSettingsFields/ViewSettingsFields';
import ViewSettingsFilter from '../viewSettingsFilter/ViewSettingsFilter';
import ViewSettingsFooter from '../viewSettingsFooter/ViewSettingsFooter';
import ViewSettingsLayout from '../viewSettingsLayout/ViewSettingsLayout';
import ViewSettingsOverview from '../viewSettingsOverview/ViewSettingsOverview';
import ViewSettingsSort from '../viewSettingsSort/ViewSettingsSort';

interface ViewPanelSettingData {
  headerTitle: string;
  contentBody: React.JSX.Element;
}

const ViewSettings: FunctionComponent = () => {
  const { contentId } = useDatabaseContent();

  if (!contentId) {
    return null;
  }

  return <ViewSettingsInternal contentId={contentId} />;
};

const ViewSettingsInternal: FunctionComponent<{ contentId: string }> = ({
  contentId,
}) => {
  const [viewNameIsValid, setViewNameIsValid] = useState<boolean>(true);
  const { view } = useView();
  const { activePanelSetting, setActivePanelSetting } = useViewUiState();
  const { hidePanel, getData, isPanelVisible } = usePanel();
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null);
  const [viewName, setViewName] = useState(view.name);

  const { formatMessage } = useIntl();
  const client = useAnalytics();

  const { autoFocusLastFilterValue, canEditViews } = getData<'ViewSettingsPanel'>();

  const handleDeleteView = useDeleteView(contentId);

  const [totalViews, viewNames, databaseFields, savedViews] =
    defaultStoreUtils.useEqualSelector((state) => {
      const database = selectDatabaseByContentIdRequired(state, { contentId });
      const filteredViews = database.views.filter((v) => v.id !== view.id);
      return [
        database.views.length,
        filteredViews.map((view) => view.name.trim().toLowerCase()),
        getDatabaseFields(database),
        database.views,
      ];
    });

  const originalView = useMemo(
    () => getViewById(savedViews, view.id),
    [savedViews, view.id]
  );

  const hasUnsavedSettingsChanges = useViewHasUnsavedChanges({ contentId });

  const hasNameChanges = useMemo(() => {
    return (
      (originalView && isUniqueViewName(viewName, [...viewNames, originalView.name])) ??
      false
    );
  }, [originalView, viewName, viewNames]);

  const hasChanges = hasUnsavedSettingsChanges || hasNameChanges;

  useEffect(() => {
    setViewNameIsValid(
      viewName.trim() !== '' && !viewNames.includes(viewName.trim().toLowerCase())
    );
  }, [originalView, viewNameIsValid, viewNames, viewName]);

  const getTriggerButton = useCallback(
    (setting: ViewPanelSettingType | null): HTMLElement | null => {
      switch (setting) {
        case ViewPanelSettingType.FILTER:
          return document.querySelector('#view-settings-filter-button');
        case ViewPanelSettingType.LAYOUT:
          return document.querySelector('#view-settings-layout-button');
        case ViewPanelSettingType.SORT:
          return document.querySelector('#view-settings-sort-button');
        case ViewPanelSettingType.FIELDS:
          return document.querySelector('#view-settings-hide-field-button');
        case ViewPanelSettingType.OVERVIEW:
          return document.querySelector('.view-actions-menu-trigger-button');
        default:
          return null;
      }
    },
    []
  );

  useEffect(() => {
    if (isPanelVisible('ViewSettingsPanel')) {
      // Store the triggering element when panel opens
      setTriggerElement(getTriggerButton(activePanelSetting));
    } else {
      setActivePanelSetting(null);
    }
  }, [isPanelVisible, setActivePanelSetting, activePanelSetting, getTriggerButton]);

  const onClose = useCallback(() => {
    client.sendViewSettingsPanelClosedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      source: 'databaseViewSettingsPanel',
      viewId: view.id,
      isEdited: hasChanges,
    });
    hidePanel();
    // Return focus to the triggering element
    setTimeout(() => {
      if (triggerElement) {
        triggerElement.focus();
      }
    }, 0);
  }, [client, contentId, hasChanges, view, hidePanel, triggerElement]);

  const newViewName = viewName !== originalView?.name ? viewName : undefined;

  const saveAsNewView = useSaveAsNewView({ contentId });

  const handleSaveAsNewView = useCallback(() => {
    saveAsNewView({ analyticsSource: 'databaseViewSettingsPanel', newViewName });
    setActivePanelSetting(ViewPanelSettingType.OVERVIEW);
  }, [newViewName, saveAsNewView, setActivePanelSetting]);

  const updateView = useUpdateView({ contentId });

  const handleViewUpdate = useCallback(async () => {
    await updateView({
      analyticsSource: 'databaseViewSettingsPanel',
      newViewName,
    });
    hidePanel();
  }, [hidePanel, newViewName, updateView]);

  const onBack = useCallback(() => {
    setActivePanelSetting(ViewPanelSettingType.OVERVIEW);
  }, [setActivePanelSetting]);

  const handleDeleteViewWithClose = useCallback(() => {
    handleDeleteView(view.id, 'databaseViewSettingsPanel', onClose);
  }, [handleDeleteView, onClose, view]);

  const isOverview = activePanelSetting === ViewPanelSettingType.OVERVIEW;

  const panelComponents: Record<ViewPanelSettingType, ViewPanelSettingData> = {
    [ViewPanelSettingType.FILTER]: {
      headerTitle: formatMessage(messages['view-settings.filter.filter-text']),
      contentBody: (
        <ViewSettingsFilter
          contentId={contentId}
          autoFocusLastFilterValue={autoFocusLastFilterValue}
        />
      ),
    },
    [ViewPanelSettingType.LAYOUT]: {
      headerTitle: formatMessage(messages['view-settings.layout.layout-heading']),
      contentBody: <ViewSettingsLayout contentId={contentId} />,
    },
    [ViewPanelSettingType.SORT]: {
      headerTitle: formatMessage(messages['view-settings.sort.sort-text']),
      contentBody: (
        <ViewSettingsSort contentId={contentId} databaseFields={databaseFields} />
      ),
    },
    [ViewPanelSettingType.FIELDS]: {
      headerTitle: formatMessage(messages['view-settings.hide-field.field-text']),
      contentBody: (
        <ViewSettingsFields contentId={contentId} databaseFields={databaseFields} />
      ),
    },
    [ViewPanelSettingType.OVERVIEW]: {
      headerTitle: formatMessage(messages['view-settings-panel.title']),
      contentBody: (
        <ViewSettingsOverview
          viewNameIsValid={viewNameIsValid}
          viewNames={viewNames}
          viewName={viewName}
          setViewName={setViewName}
          canEditViews={canEditViews}
        />
      ),
    },
  };

  useEffect(() => {
    setViewName(view.name);
  }, [view.name, setViewName]);

  const canDelete =
    isOverview && canEditViews && view.id !== ViewId.UNSAVED && totalViews > 1;

  return (
    <PanelLayout
      testId="view-settings-panel-content"
      header={{
        title:
          panelComponents[
            activePanelSetting ? activePanelSetting : ViewPanelSettingType.OVERVIEW
          ].headerTitle,
        actions: {
          onClose,
          backButton: !isOverview && <BackButton onBack={onBack} />,
          closeButtonTooltipMessage: hasChanges
            ? formatMessage(messages['view-settings-panel.close.tooltip-text'])
            : undefined,
          copyButton: isOverview && <CopyLinkButton contentId={contentId} />,
          deleteButton: canDelete && (
            <DeleteViewButton onDelete={handleDeleteViewWithClose} />
          ),
        },
      }}
      content={
        panelComponents[
          activePanelSetting ? activePanelSetting : ViewPanelSettingType.OVERVIEW
        ].contentBody
      }
      footer={
        <ViewSettingsFooter
          contentId={contentId}
          handleViewUpdate={handleViewUpdate}
          handleSaveAsNewView={handleSaveAsNewView}
          viewNameIsValid={viewNameIsValid}
          hasChanges={hasChanges}
          canEditViews={canEditViews}
        />
      }
    />
  );
};

export default ViewSettings;
